import React, { useEffect, useState } from 'react';
import { Phone, ClipboardCheck, ChevronRight, Shield } from 'lucide-react';
import { businessInfo } from '../../data/mock';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,212,255,0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,212,255,0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00D4FF]/10 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#00D4FF]/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 mb-8 backdrop-blur-sm">
              <Shield className="w-4 h-4 text-[#00D4FF]" />
              <span className="text-sm text-gray-300">Florida's Premier Armed Security</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-white">Elite Armed Security</span>
              <br />
              <span className="text-[#00D4FF]">Officers Across Florida</span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-gray-400 mb-10 max-w-xl leading-relaxed">
              Licensed. Trained. Trusted. Rapid deployment statewide with
              professionally vetted armed security personnel.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${businessInfo.phone}`}
                className="group flex items-center justify-center gap-3 bg-[#00D4FF] text-black px-8 py-4 font-semibold text-lg hover:shadow-[0_0_40px_rgba(0,212,255,0.5)] transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                Call {businessInfo.phone}
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#contact"
                className="group flex items-center justify-center gap-3 bg-white/10 text-white px-8 py-4 font-semibold text-lg hover:bg-white hover:text-black transition-all duration-300 border border-white/20"
              >
                <ClipboardCheck className="w-5 h-5" />
                Free Security Assessment
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="text-3xl font-bold text-[#00D4FF]">500+</p>
                <p className="text-sm text-gray-500">Active Officers</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <p className="text-3xl font-bold text-[#00D4FF]">24/7</p>
                <p className="text-sm text-gray-500">Availability</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <p className="text-3xl font-bold text-[#00D4FF]">50+</p>
                <p className="text-sm text-gray-500">Cities Covered</p>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative">
              {/* Main Visual Box */}
              <div className="relative bg-gradient-to-br from-white/10 to-transparent border border-white/10 p-8 backdrop-blur-sm">
                <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-[#00D4FF]" />
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-[#00D4FF]" />
                
                <div className="aspect-square bg-gradient-to-br from-[#0a1628] to-[#000] flex items-center justify-center relative overflow-hidden">
                  {/* Shield Icon */}
                  <div className="relative z-10">
                    <Shield className="w-32 h-32 text-[#00D4FF] opacity-80" strokeWidth={1} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-bold text-white">FL</span>
                    </div>
                  </div>
                  
                  {/* Animated rings */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 border border-[#00D4FF]/20 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-64 border border-[#00D4FF]/10 rounded-full animate-ping" style={{ animationDuration: '4s' }} />
                  </div>
                </div>

                {/* Info Cards */}
                <div className="absolute -bottom-4 -left-4 bg-[#00D4FF] text-black p-4 min-w-[180px]">
                  <p className="text-xs font-medium opacity-70">FLORIDA STATE</p>
                  <p className="text-lg font-bold">LICENSED & INSURED</p>
                </div>

                <div className="absolute -top-4 -right-4 bg-black border border-white/20 p-4 backdrop-blur-sm">
                  <p className="text-xs text-gray-400">RESPONSE TIME</p>
                  <p className="text-2xl font-bold text-[#00D4FF]">RAPID</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-gray-500 uppercase tracking-wider">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-[#00D4FF] to-transparent" />
      </div>
    </section>
  );
};

export default HeroSection;
